import React, { useEffect, useState, useRef, useMemo } from "react";
import Layout from '../../components/layout/Layout';
import { getData } from "../../services/apiService";
import GroupIcon from "@mui/icons-material/Group";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import SchoolIcon from "@mui/icons-material/School";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import {
    Avatar, Box, Card, CardContent, Chip, IconButton, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography, useMediaQuery, useTheme, Autocomplete, TextField
} from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, } from "recharts";
import { Pie } from "react-chartjs-2";
import { ArcElement, Chart as ChartJS, Legend as ChartLegend, Tooltip as ChartTooltip } from "chart.js";

ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

const InstituteDashboard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [anchorEl, setAnchorEl] = useState(null);
    const isFetched = useRef(false);
    const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
    const [activeStudents, setActiveStudents] = useState([]);
    const [activeVoucher, setActiveVoucher] = useState([]);
    const [totalCourse, setTotalCourse] = useState([]);
    const [pending, setPending] = useState([]);
    const [allStudent, setAllStudent] = useState([]);
    const [allCourse, setAllCourse] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [courseData, setCourseData] = useState([])
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [yearFilter, setYearFilter] = useState('');
    const [progressStats, setProgressStats] = useState({ notStarted: 0, inProgress: 0, completed: 0 });
    const normalizedProgressStats = useMemo(() => {
        const notStarted = Number(progressStats.notStarted) || 0;
        const inProgress = Number(progressStats.inProgress) || 0;
        const completed = Number(progressStats.completed) || 0;
        const safeTotal = notStarted + inProgress + completed;

        if (safeTotal <= 0) {
            return {
                notStarted: 0,
                inProgress: 0,
                completed: 0,
                hasData: false
            };
        }

        return {
            notStarted: Math.max(0, notStarted),
            inProgress: Math.max(0, inProgress),
            completed: Math.max(0, completed),
            hasData: true
        };
    }, [progressStats]);

    const progressChartData = {
        labels: ["Not Started", "In Progress", "Completed"],
        datasets: [
            {
                data: [
                    normalizedProgressStats.notStarted,
                    normalizedProgressStats.inProgress,
                    normalizedProgressStats.completed
                ],
                backgroundColor: normalizedProgressStats.hasData
                    ? ["#EF5350", "#FFCA28", "#66BB6A"]
                    : ["#CFD8DC"],
                borderWidth: 1,
                borderColor: "#ffffff"
            }
        ]
    };

    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 4 }, (_, i) => (currentYear - i).toString());
    }, []);

    useEffect(() => {
        if (!isFetched.current) {
            fetchData();
            getCoursesForDropdown();
            isFetched.current = true;
        }
    }, []);

    useEffect(() => {
        if (isFetched.current) {
            getStudentProgressStats();
        }
    }, [selectedCourse?._id, yearFilter]);


    useEffect(() => {
        const currentYear = new Date().getFullYear().toString();
        setYearFilter(currentYear);
    }, []);


    const fetchData = async () => {
        try {
            const response = await getData("/get-institute-dashboard-data");
            if (response) {
                setActiveVoucher(response.activeVoucher);
                setAllStudent(response.allStudent);
                setActiveStudents(response.activeStudents);
                setTotalCourse(response.totalCourse);
                setPending(response.pending);
                setAllCourse(response.allCourse);
                setChartData(response.studentChartData);
            }
        } catch (error) {
            console.error("Failed to fetch data", error);
        }
    };


    const getStudentProgressStats = async () => {

        try {
            const params = new URLSearchParams();

            if (selectedCourse?._id) {
                params.append("courseId", selectedCourse._id);
            }

            if (yearFilter) {
                params.append("year", yearFilter);
            }

            const response = await getData(`/get-college-student-progress?${params.toString()}`);
            // const response = await getData(`/get-student-progress-stats?${params.toString()}`);

            if (response) {
                setProgressStats({
                    notStarted: parseFloat(response.notStarted) || 0,
                    inProgress: parseFloat(response.inProgress) || 0,
                    completed: parseFloat(response.completed) || 0
                });
            }

        } catch (error) {
            console.error(error);

        }
    };

    const getCoursesForDropdown = async () => {
        try {
            const response = await getData("/get-courses-for-dropdown");
            if (response) {
                setCourseData(response)
            }
        } catch (error) {
            console.error("Failed to fetch data", error);
        }
    }

    const metricsData = [
        {
            title: "Total Courses Running",
            value: Array.isArray(totalCourse) ? totalCourse.length : totalCourse || 0,
            icon: <SchoolIcon />,
            color: "#FF6B6B",
            bgColor: "#FFE5E5",
        },
        {
            title: "Active Students",
            value: Array.isArray(activeStudents) ? activeStudents.length : activeStudents || 0,
            icon: <GroupIcon />,
            color: "#4ECDC4",
            bgColor: "#E5F9F7",
        },
        {
            title: "Active Voucher",
            value: Array.isArray(activeVoucher) ? activeVoucher.length : activeVoucher || 0,
            icon: <SupervisorAccountIcon />,
            color: "#45B7D1",
            bgColor: "#E5F4FD",
        },
        {
            title: "Pending Approvals",
            value: Array.isArray(pending) ? pending.length : pending || 0,
            icon: <PendingActionsIcon />,
            color: "#9B59B6",
            bgColor: "#F0E6FF",
        },
    ];

    return (
        <Layout>
            <div className="bg-gray-100 min-h-screen">
                {/* <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        p: { xs: 2, sm: 3 },
                        width: { sm: `calc(116% - ${drawerWidth}px)` },
                    }}
                > */}

                <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, width: "100%", maxWidth: "100%", mx: "auto", }} >

                    {/* Metrics Cards */}
                    <Box
                        sx={{
                            mb: { xs: 2, sm: 3 },
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                lg: "repeat(4, minmax(0, 1fr))"
                            },
                            gap: 3
                        }}
                    >
                        {metricsData.map((metric, index) => (
                            <Box key={index}>
                                <Card
                                    sx={{
                                        border: "none",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                                        minHeight: { xs: 120, sm: 140, md: 100 },
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            p: { xs: 1, sm: 1.5, md: 2 },
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                                flexGrow: 1,
                                            }}
                                        >
                                            <Box sx={{ maxWidth: "72%" }}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        mb: 0.6,
                                                        fontSize: {
                                                            xs: "0.7rem",
                                                            sm: "0.8rem",
                                                            md: "0.875rem",
                                                        },
                                                        lineHeight: 1.4,
                                                    }}
                                                >
                                                    {metric.title}
                                                </Typography>
                                                <Typography
                                                    variant="h5"
                                                    sx={{
                                                        mb: 0.5,
                                                        fontSize: {
                                                            xs: "1.1rem",
                                                            sm: "1.25rem",
                                                            md: "1.4rem",
                                                        },
                                                        fontWeight: "bold",
                                                        lineHeight: 1.35,
                                                    }}
                                                >
                                                    {metric.value}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        fontSize: {
                                                            xs: "0.6rem",
                                                            sm: "0.7rem",
                                                            md: "0.75rem",
                                                        },
                                                        lineHeight: 1.5,
                                                    }}
                                                >
                                                    {metric.subtitle}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ p: 1, borderRadius: 1, bgcolor: metric.bgColor, color: metric.color, }} >
                                                {metric.icon}
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                        ))}
                    </Box>

                    {/* Main Layout */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                lg: "minmax(0, 2fr) minmax(320px, 1fr)"
                            },
                            gap: { xs: 1, sm: 2, md: 3 },
                            width: "100%"
                        }}
                    >
                        {/* Left Section */}
                        <Box sx={{ minWidth: 0 }}>
                            {/* Course Overview */}
                            {/* <Card
                                sx={{
                                    mb: { xs: 2, sm: 3 },
                                    border: "none",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                }}
                            > */}

                            <Card
                                sx={{
                                    mb: { xs: 2, sm: 3 },
                                    border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                    height: 420, display: "flex", flexDirection: "column",
                                }}
                            >
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Box
                                        sx={{
                                            display: "flex", justifyContent: "space-between",
                                            alignItems: { xs: "flex-start", sm: "center" }, mb: 2,
                                            flexDirection: { xs: "column", sm: "row" }, gap: { xs: 1, sm: 0 },
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="h5" sx={{ mb: 1, fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem", }, }} >
                                                Course Overview
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }} >
                                                All running courses.
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* <TableContainer sx={{ maxHeight: { xs: 300, sm: 400 } }}> */}
                                    <TableContainer
                                        sx={{
                                            height: 280, overflowY: "auto", borderRadius: 2,

                                            "&::-webkit-scrollbar": {
                                                width: "6px",
                                            },
                                            "&::-webkit-scrollbar-thumb": {
                                                backgroundColor: "#c1c1c1",
                                                borderRadius: "10px",
                                            },
                                        }}
                                    >
                                        <Table size={isMobile ? "small" : "medium"}>
                                            <TableHead>
                                                <TableRow>
                                                    {["Course", "Status"].map(
                                                        (col, i) => (
                                                            <TableCell
                                                                key={i}
                                                                sx={{
                                                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                                                    padding: { xs: "8px", sm: "16px" },
                                                                }}
                                                            >
                                                                {col}
                                                            </TableCell>
                                                        )
                                                    )}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {allCourse.map((course, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell
                                                            sx={{
                                                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                                                padding: { xs: "8px", sm: "16px" },
                                                            }}
                                                        >
                                                            {course?.course?.courseName || "N/A"}
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                                                padding: { xs: "8px", sm: "16px" },
                                                            }}
                                                        >
                                                            <Chip
                                                                label={course.status}
                                                                color={
                                                                    course.status === "Active"
                                                                        ? "success"
                                                                        : "warning"
                                                                }
                                                                size="small"
                                                                sx={{
                                                                    fontSize: { xs: "0.6rem", sm: "0.75rem" },
                                                                }}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>

                            {/* Student Insights */}
                            {/* <Card
                                sx={{
                                    border: "none",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                }}
                            > */}
                            <Card
                                sx={{
                                    border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                    height: 520, display: "flex", flexDirection: "column",
                                }}
                            >
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Box
                                        sx={{
                                            display: "flex", justifyContent: "space-between",
                                            alignItems: { xs: "flex-start", sm: "center" }, mb: 2,
                                            flexDirection: { xs: "column", sm: "row" }, gap: { xs: 1, sm: 0 },
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    mb: 1,
                                                    fontSize: {
                                                        xs: "1.1rem",
                                                        sm: "1.25rem",
                                                        md: "1.5rem",
                                                    },
                                                }}
                                            >
                                                Student Insights
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                                            >
                                                Active vs. Inactive Students
                                            </Typography>
                                        </Box>
                                        <IconButton onClick={handleMenuClick}>
                                            <MoreVertIcon
                                                fontSize={isMobile ? "small" : "medium"}
                                            />
                                        </IconButton>
                                    </Box>

                                    {/* <Box sx={{ height: { xs: 250, sm: 300 }, mt: 2 }}> */}
                                    <Box
                                        sx={{
                                            flexGrow: 1, height: 350, width: "100%", mt: 2,
                                        }}
                                    >
                                        {/* <ResponsiveContainer width="100%" height="100%"> */}
                                        <ResponsiveContainer width="100%" height={350}>
                                            <BarChart
                                                data={chartData}
                                                // margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="month" tick={{ fontSize: isMobile ? 10 : 12 }} />
                                                <YAxis tick={{ fontSize: isMobile ? 10 : 12 }} />
                                                <Tooltip />
                                                <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12, paddingTop: 10, }} />
                                                <Bar dataKey="active" fill="#4B49AC" name="Active Students" />
                                                <Bar dataKey="inactive" fill="#98BDFE" name="Inactive Students" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>

                        {/* Right Section */}
                        <Box sx={{ minWidth: 0 }}>
                            {/* Instructor Management */}
                            {/* <Card
                                sx={{
                                    mb: { xs: 2, sm: 3 },
                                    border: "none",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                }}
                            > */}

                            <Card
                                sx={{
                                    mb: { xs: 2, sm: 3 }, border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                    height: 420, display: "flex", flexDirection: "column",
                                }}
                            >
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Box
                                        sx={{
                                            display: "flex", justifyContent: "space-between",
                                            alignItems: { xs: "flex-start", sm: "center" },
                                            mb: 2, flexDirection: { xs: "column", sm: "row" }, gap: { xs: 1, sm: 0 },
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    mb: 1,
                                                    fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem", },
                                                }}
                                            >
                                                Students
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }} >
                                                Last 10 Students
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Scrollable Box */}
                                    {/* <Box
                                        sx={{
                                            maxHeight: 240, // adjust based on row height (≈ 60px * 4 rows)
                                            overflowY: "auto",
                                            pr: 1, // padding for scrollbar
                                        }}
                                    > */}
                                    <Box
                                        sx={{
                                            height: 280, overflowY: "auto", pr: 1,

                                            "&::-webkit-scrollbar": {
                                                width: "6px",
                                            },
                                            "&::-webkit-scrollbar-thumb": {
                                                backgroundColor: "#c1c1c1",
                                                borderRadius: "10px",
                                            },
                                        }}
                                    >
                                        <Stack spacing={2}>
                                            {allStudent.map((student, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: "flex", alignItems: "center",
                                                        justifyContent: "space-between", p: 1, borderRadius: 1,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 },
                                                        }}
                                                    >
                                                        <Avatar
                                                            src={student.avatar}
                                                            sx={{
                                                                width: { xs: 32, sm: 40 },
                                                                height: { xs: 32, sm: 40 },
                                                            }}
                                                        />
                                                        <Box>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                                                }}
                                                            >
                                                                {student.firstName}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Chip
                                                        label={
                                                            student.isApproved === true || student.isApproved === "true"
                                                                ? "Active"
                                                                : "Inactive"
                                                        }
                                                        color={
                                                            student.isApproved === true || student.isApproved === "true"
                                                                ? "success"
                                                                : "default"
                                                        }
                                                        size="small"
                                                        sx={{
                                                            fontSize: { xs: "0.6rem", sm: "0.75rem" },
                                                        }}
                                                    />
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* <Card
                                sx={{
                                    borderRadius: 3,

                                    display: "flex",
                                    flexDirection: "column",

                                    boxShadow: "rgba(0,0,0,0.06) 0px 4px 12px",
                                }}
                            > */}

                            <Card
                                sx={{
                                    borderRadius: 3, height: 520, display: "flex",
                                    flexDirection: "column", boxShadow: "rgba(0,0,0,0.06) 0px 4px 12px",
                                }}
                            >
                                <CardContent
                                    sx={{
                                        p: 2, display: "flex", flexDirection: "column", flexGrow: 1,
                                    }}
                                >
                                    {/* Header */}
                                    <Stack
                                        direction="column" justifyContent="space-between"
                                        alignItems="flex-start" sx={{ mb: 2, gap: 2 }}
                                    >
                                        {/* LEFT SIDE (Title) */}
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                Student Progress Status
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Overall learning progress distribution
                                            </Typography>
                                        </Box>

                                        {/* RIGHT SIDE (Filters) */}
                                        <Box
                                            sx={{
                                                width: "100%", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                                                gap: 1.5
                                            }}
                                        >
                                            {/* Course */}
                                            <Autocomplete
                                                size="small" fullWidth options={courseData}
                                                getOptionLabel={(option) => option.courseName || ""}
                                                value={selectedCourse} isOptionEqualToValue={(option, value) => option._id === value._id}
                                                onChange={(e, newValue) => setSelectedCourse(newValue)}
                                                renderInput={(params) => (<TextField {...params} label="Course" />)}
                                            />

                                            {/* Year */}
                                            <Autocomplete size="small" fullWidth options={yearOptions}
                                                getOptionLabel={(option) => option || ""}
                                                value={yearFilter} isOptionEqualToValue={(option, value) => option === value}
                                                onChange={(e, newValue) => setYearFilter(newValue)}
                                                renderInput={(params) => (<TextField {...params} label="Year" />)}
                                            />
                                        </Box>
                                    </Stack>
                                    {/* Chart Centered */}
                                    <Box
                                        sx={{
                                            display: "flex", justifyContent: "center", alignItems: "center",
                                            flexGrow: 1, minHeight: { xs: 220, sm: 240, md: 260 },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: { xs: "100%", sm: 220, md: 240 }, maxWidth: 240,
                                                height: { xs: 200, sm: 220, md: 240 }
                                            }}
                                        >
                                            <Pie
                                                data={progressChartData}
                                                options={{
                                                    maintainAspectRatio: false,
                                                    cutout: "55%",
                                                    plugins: {
                                                        legend: { display: false },
                                                        tooltip: {
                                                            callbacks: {
                                                                label: function (context) {
                                                                    return `${context.label}: ${context.parsed}%`;
                                                                }
                                                            }
                                                        }
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    {/* Bottom Stats */}
                                    <Stack
                                        direction="row" spacing={2} justifyContent="center"
                                        alignItems="center" sx={{ mt: 2, flexWrap: "wrap" }}
                                    >
                                        {/* Not Started */}
                                        <Box
                                            sx={{
                                                minWidth: 120, px: 2, py: 1, borderRadius: 5, backgroundColor: "#FFEBEE",
                                                display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1,
                                            }}
                                        >
                                            <Typography sx={{ color: "#F44336", fontSize: 13 }}>
                                                Not Started
                                            </Typography>
                                            <Typography fontWeight={600}>
                                                {normalizedProgressStats.notStarted}%
                                            </Typography>
                                        </Box>

                                        {/* In Progress */}
                                        <Box
                                            sx={{
                                                minWidth: 120, px: 2, py: 1, borderRadius: 5,
                                                backgroundColor: "#FFF8E1", display: "flex",
                                                justifyContent: "space-between", alignItems: "center", gap: 1,
                                            }}
                                        >
                                            <Typography sx={{ color: "#FFC107", fontSize: 13 }}>
                                                In Progress
                                            </Typography>
                                            <Typography fontWeight={600}>
                                                {normalizedProgressStats.inProgress}%
                                            </Typography>
                                        </Box>

                                        {/* Completed */}
                                        <Box
                                            sx={{
                                                minWidth: 120, px: 2, py: 1, borderRadius: 5,
                                                backgroundColor: "#E8F5E9", display: "flex",
                                                justifyContent: "space-between", alignItems: "center", gap: 1,
                                            }}
                                        >
                                            <Typography sx={{ color: "#4CAF50", fontSize: 13 }}>
                                                Completed
                                            </Typography>
                                            <Typography fontWeight={600}>
                                                {normalizedProgressStats.completed}%
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Box>


                    </Box>
                </Box>
            </div>
        </Layout>
    );
};

export default InstituteDashboard;
